import React, { useRef, useState } from "react";
import axios from "axios";
import { UploadCloud, Loader2, X, Image as ImageIcon, Video as VideoIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const absolutize = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url}`;
};

/**
 * FileUpload — allows admin to either upload a file OR paste an external URL.
 * onChange(value) is called with the final URL (absolute, ready to render).
 */
export default function FileUpload({ label = "Image", value, onChange, accept = "image/*", kind = "image" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [urlInput, setUrlInput] = useState("");

// Cloudflare's proxy caps client → origin uploads at ~100 MB. Refuse anything
// bigger client-side so the admin sees a clear "compress your video" message
// instead of a scary Cloudflare 520/524/Failed error.
const CLOUDFLARE_UPLOAD_CAP_MB = 95;

  const handleFile = async (file) => {
    if (!file) return;
    // Guard against Cloudflare-cap failures before we even start the upload.
    if (file.size && file.size > CLOUDFLARE_UPLOAD_CAP_MB * 1024 * 1024) {
      const mb = (file.size / (1024 * 1024)).toFixed(0);
      setError(
        `This file is ${mb} MB — the site's uploader caps at ${CLOUDFLARE_UPLOAD_CAP_MB} MB (Cloudflare limit). ` +
          "Please compress the video (a 30-second clip should be under 30 MB) using your phone's built-in " +
          "trimmer or a free tool like HandBrake, then try again."
      );
      return;
    }
    setUploading(true);
    setError("");
    setNotice("");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/admin/upload`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.url);
      if (res.data.warning) setNotice(res.data.warning);
    } catch (err) {
      const status = err && err.response && err.response.status;
      const detail =
        err && err.response && err.response.data && err.response.data.detail;
      let msg = detail || (err && err.message) || "Upload failed";
      if (status === 401 || status === 403) {
        msg = "Not signed in as admin — please log in again.";
      } else if (status === 413) {
        msg = "File is too large. Try a smaller image or trim the video.";
      } else if (status === 520 || status === 524 || status === 522 || status === 504) {
        msg =
          "Upload rejected by Cloudflare (video is too large or took too long). " +
          "Please compress the video to under 90 MB or 30 seconds and try again.";
      } else if (!status) {
        msg = "Network error — check your connection and try again.";
      }
      setError(`${msg}${status ? ` (HTTP ${status})` : ""}`);
    } finally {
      setUploading(false);
    }
  };

  const applyUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    onChange(u);
    setUrlInput("");
  };

  const clear = () => onChange("");

  const preview = value ? absolutize(value) : "";
  const isVideo = kind === "video" || /\.(mp4|mov|webm)(\?|$)/i.test(value || "");

  return (
    <div className="fu-wrap">
      <div className="fu-label">{label}</div>

      {preview ? (
        <div className="fu-preview">
          {isVideo ? (
            <video src={preview} muted playsInline controls />
          ) : (
            <img src={preview} alt={label} />
          )}
          <button type="button" className="fu-clear" onClick={clear} aria-label="Remove">
            <X size={14} />
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="fu-drop"
          onClick={() => inputRef.current && inputRef.current.click()}
        >
          {uploading ? (
            <>
              <Loader2 size={22} className="fu-spin" />
              <span>Uploading…</span>
            </>
          ) : (
            <>
              {isVideo ? <VideoIcon size={22} /> : <ImageIcon size={22} />}
              <span>
                <b>Choose {kind}</b> — or drop here
              </span>
              <em>JPG · PNG · WEBP · HEIC · MP4 · MOV — we auto-optimise for the web</em>
            </>
          )}
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files && e.target.files[0];
          if (f) handleFile(f);
          e.target.value = "";
        }}
      />

      <div className="fu-or">
        <span>or paste a URL</span>
      </div>
      <div className="fu-url-row">
        <input
          type="url"
          placeholder="https://…"
          value={urlInput}
          onChange={(e) => setUrlInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              applyUrl();
            }
          }}
        />
        <button type="button" className="fu-url-btn" onClick={applyUrl}>
          <UploadCloud size={14} /> Use
        </button>
      </div>

      {error && <div className="fu-error">{error}</div>}
      {notice && (
        <div
          className="fu-notice"
          style={{
            marginTop: 8,
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(232,180,120,0.16)",
            border: "1px solid rgba(232,180,120,0.45)",
            color: "#5c3a12",
            fontSize: 12,
            lineHeight: 1.45,
          }}
          data-testid="fu-notice"
        >
          {notice}
        </div>
      )}
    </div>
  );
}

export { absolutize };
